import React, { useState, useEffect } from 'react';
import { getAllProjects, deleteProject } from '../utils/projects';
import AdminLayout from '../layouts/AdminLayout';
import { FiX, FiFolder, FiGithub, FiExternalLink, FiTrash2, FiCheck, FiAlertTriangle, FiEye, FiLayers, FiVideo } from 'react-icons/fi';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState('');
  const [message, setMessage] = useState({});
  const [selectedProject, setSelectedProject] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFilteredProjects(projects);
  }, [projects]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError('');

      const result = await getAllProjects();
      if (result.success) {
        setProjects(result.data);
        setFilteredProjects(result.data);
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('حدث خطأ في جلب بيانات المشاريع');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      const result = await deleteProject(projectId);
      setMessage({ type: result.success ? 'success' : 'error', text: result.message });
      
      if (result.success) {
        setProjects(prev => prev.filter(project => project.id !== projectId));
      }
      setShowDeleteModal(null);
      setTimeout(() => setMessage({}), 3000);
    } catch (err) {
      console.error('Error deleting project:', err);
      setMessage({ type: 'error', text: 'حدث خطأ أثناء حذف المشروع' });
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow mx-auto mb-4"></div>
            <p className="text-yellow">جاري التحميل...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-yellow mb-2">
          إدارة <span className="text-white">المشاريع</span>
        </h1>
        <p className="text-white/70">مراجعة المشاريع المقدمة من الفرق</p>
      </div>

      {/* Success/Error Message */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg flex items-center justify-between border-2 ${
          message.type === 'success'
            ? 'bg-green-500/20 border-green-500 text-green-400'
            : 'bg-red-500/20 border-red-500 text-red-400'
        }`}>
          <div className="flex items-center gap-2">
            {message.type === 'success' ? <FiCheck /> : <FiX />}
            <span>{message.text}</span>
          </div>
          <button onClick={() => setMessage({})} className="text-white/70 hover:text-white">
            <FiX />
          </button>
        </div>
      )}

      {/* Projects Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredProjects.map((project) => (
          <div key={project.id} className="bg-dark-blue border-2 border-yellow rounded-2xl overflow-hidden hover:border-white/50 transition-all duration-300">
            {/* Card Header */}
            <div className="bg-yellow px-6 py-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-[#05339c] p-2 rounded-lg">
                    <FiFolder className="text-yellow" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#05339c] line-clamp-1">{project.team?.team_name || 'بدون فريق'}</h3>
                    <p className="text-[#05339c]/70 text-xs mt-1">
                      {project.team?.leader?.full_name || 'غير محدد'}
                    </p>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold border-2 ${
                  project.submitted
                    ? 'bg-green-500/20 text-green-400 border-green-500'
                    : 'bg-gray-500/20 text-gray-400 border-gray-500'
                }`}>
                  {project.submitted ? 'مقدم' : 'غير مقدم'}
                </span>
              </div>
            </div>

            {/* Card Body */}
            <div className="p-6 space-y-4">
              {/* Description */}
              <div>
                <p className="text-xs text-white/50 mb-2">وصف المشروع</p>
                <p className="text-sm text-white/80 line-clamp-3">{project.short_description}</p>
              </div>

              {/* Links */}
              <div className="space-y-2">
                {project.github_url && (
                  <a
                    href={project.github_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-white/80 hover:text-yellow transition-colors"
                  >
                    <FiGithub size={16} />
                    <span className="truncate">GitHub</span>
                    <FiExternalLink size={12} className="mr-auto" />
                  </a>
                )}
                {project.demo_url && (
                  <a
                    href={project.demo_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-white/80 hover:text-yellow transition-colors"
                  >
                    <FiExternalLink size={16} />
                    <span className="truncate">العرض التوضيحي</span>
                    <FiExternalLink size={12} className="mr-auto" />
                  </a>
                )}
                {project.additional_files && (
                  <a
                    href={project.additional_files}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-white/80 hover:text-yellow transition-colors"
                  >
                    <FiVideo size={16} />
                    <span className="truncate">الفيديو</span>
                    <FiExternalLink size={12} className="mr-auto" />
                  </a>
                )}
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t border-white/10">
                <button
                  onClick={() => setSelectedProject(project)}
                  className="flex items-center gap-2 px-3 py-2 bg-blue text-yellow border-2 border-yellow rounded-lg hover:bg-blue/80 transition-colors text-sm font-medium"
                >
                  <FiEye size={16} />
                  <span>تفاصيل</span>
                </button>

                <button
                  onClick={() => setShowDeleteModal(project)}
                  className="flex items-center gap-2 px-3 py-2 bg-red-500/20 text-red-400 border-2 border-red-500 rounded-lg hover:bg-red-500/30 transition-colors text-sm font-medium mr-auto"
                >
                  <FiTrash2 size={16} />
                  <span>حذف</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredProjects.length === 0 && (
        <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-12 text-center">
          <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiFolder className="text-white/50" size={32} />
          </div>
          <p className="text-white mb-2">لا توجد مشاريع مطابقة</p>
          <p className="text-white/60 text-sm">لم يتم تقديم أي مشاريع بعد</p>
        </div>
      )}

      {/* Project Details Modal */}
      {selectedProject && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-yellow px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-[#05339c]">{selectedProject.team?.team_name || 'مشروع'}</h3>
                <p className="text-[#05339c]/70 text-sm">تفاصيل المشروع</p>
              </div>
              <button
                onClick={() => setSelectedProject(null)}
                className="text-[#05339c] hover:bg-[#05339c] hover:text-yellow p-2 rounded-lg transition-all"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status */}
              <div className="bg-blue rounded-xl p-4 border-2 border-white/10">
                <p className="text-xs text-white/50 mb-2">حالة التقديم</p>
                <span className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold border-2 ${
                  selectedProject.submitted
                    ? 'bg-green-500/20 text-green-400 border-green-500'
                    : 'bg-gray-500/20 text-gray-400 border-gray-500'
                }`}>
                  {selectedProject.submitted ? '✓ مقدم' : 'غير مقدم'}
                </span>
              </div>

              {/* Team Info */}
              <div>
                <h4 className="font-semibold text-yellow mb-3 flex items-center gap-2">
                  <FiLayers />
                  معلومات الفريق
                </h4>
                <div className="bg-blue rounded-xl p-4 space-y-2 border-2 border-white/10">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/60">اسم الفريق</span>
                    <span className="font-semibold text-white">{selectedProject.team?.team_name || 'غير محدد'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/60">قائد الفريق</span>
                    <span className="font-semibold text-white">{selectedProject.team?.leader?.full_name || 'غير محدد'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white/60">البريد الإلكتروني</span>
                    <span className="font-medium text-white/80 text-sm">{selectedProject.team?.leader?.email || 'غير محدد'}</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <h4 className="font-semibold text-yellow mb-3">وصف المشروع</h4>
                <div className="bg-blue rounded-xl p-4 border-2 border-white/10">
                  <p className="text-white/80 leading-relaxed">{selectedProject.short_description}</p>
                </div>
              </div>

              {/* Links */}
              <div>
                <h4 className="font-semibold text-yellow mb-3">الروابط</h4>
                <div className="space-y-3">
                  {selectedProject.github_url && (
                    <a
                      href={selectedProject.github_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-4 bg-blue rounded-xl hover:bg-blue/80 transition-colors border-2 border-white/10"
                    >
                      <div className="flex items-center gap-3">
                        <FiGithub size={20} className="text-white" />
                        <span className="font-medium text-white">GitHub Repository</span>
                      </div>
                      <FiExternalLink className="text-white/50" />
                    </a>
                  )}
                  {selectedProject.demo_url && (
                    <a
                      href={selectedProject.demo_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-4 bg-blue rounded-xl hover:bg-blue/80 transition-colors border-2 border-white/10"
                    >
                      <div className="flex items-center gap-3">
                        <FiExternalLink size={20} className="text-white" />
                        <span className="font-medium text-white">العرض التوضيحي</span>
                      </div>
                      <FiExternalLink className="text-white/50" />
                    </a>
                  )}
                  {selectedProject.additional_files && (
                    <a
                      href={selectedProject.additional_files}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-4 bg-blue rounded-xl hover:bg-blue/80 transition-colors border-2 border-white/10"
                    >
                      <div className="flex items-center gap-3">
                        <FiVideo size={20} className="text-white" />
                        <span className="font-medium text-white">فيديو العرض التقديمي</span>
                      </div>
                      <FiExternalLink className="text-white/50" />
                    </a>
                  )}
                  {!selectedProject.github_url && !selectedProject.demo_url && !selectedProject.additional_files && (
                    <p className="text-center text-white/60 py-4">لا توجد روابط متاحة</p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                <button
                  onClick={() => {
                    setSelectedProject(null);
                    setShowDeleteModal(selectedProject);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-500/20 text-red-400 border-2 border-red-500 rounded-xl hover:bg-red-500/30 transition-colors font-semibold"
                >
                  <FiTrash2 size={20} />
                  <span>حذف المشروع</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl max-w-md w-full">
            <div className="p-6">
              <div className="w-12 h-12 bg-red-500/20 border-2 border-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiAlertTriangle className="text-red-400" size={24} />
              </div>
              <h3 className="text-xl font-bold text-yellow text-center mb-2">
                تأكيد الحذف
              </h3>
              <p className="text-white/70 text-center mb-6">
                هل أنت متأكد من حذف مشروع <strong className="text-white">{showDeleteModal.team?.team_name}</strong>؟<br />
                لا يمكن التراجع عن هذا الإجراء.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(null)}
                  className="flex-1 px-4 py-3 bg-blue text-white border-2 border-white/20 rounded-xl hover:bg-blue/80 transition-colors font-semibold"
                >
                  إلغاء
                </button>
                <button
                  onClick={() => handleDeleteProject(showDeleteModal.id)}
                  className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors font-semibold"
                >
                  حذف
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Projects;
